import React, { memo, useEffect, useState } from 'react'
import { INumSchema } from '../../../../../Num/Num'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import {
    buildControlsExt,
    numberInput,
} from '../../../../../../inputs/controls'
import { numberInputErrors } from '../../../../../../../utils/validationErrors/numberInputErrors'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: INumSchema
    name: string // key in schema
    wrapper?: string // e.g. 'block_dictionary'
}

export const RuleNum = memo(function RuleNum({
    dataSchema,
    name,
    dataPath,
    wrapper,
    pathWithElementID,
}: Props) {
    const dispatch = useAppDispatch()
    const patSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(patSplit))
    const path = [...patSplit, name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        default: defaultValue,
        description,
        name: inputLabel,
        type,
    } = dataSchema
    const validationCheck = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck }, name)
    const label = getLabel(name, inputLabel)
    const className = !wrapper ? 'w-100' : ''
    const errorKey = pathWithElementID
        ? [...pathWithElementID?.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty = Object.keys(configBit || {}).length
    const isUndefined = typeof configBit?.[name] === 'undefined'
    const isEmpty = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (isChecked && dataPath?.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [dispatch]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                data: configBit,
                name,
                path,
                dataSchema,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        numberInput({
            name: name,
            description,
            label,
            className,
            isColumn: true,
            errMsg: numberInputErrors(inputState?.[name], dataSchema),
            isRequired,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const NumInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{NumInput}</div>
})
